/**
 * Project save/load, autosave/restore, and dirty-state title indicator.
 *
 * RoutePlotter prototype mixin: methods moved verbatim out of main.js
 * (Phase 1 enabling refactor). Every method runs with `this` bound to the
 * RoutePlotter instance; main.js attaches the group via
 * Object.assign(RoutePlotter.prototype, persistenceMixin).
 */
import { finishRenderReference, hasReferenceMotion } from './finishContexts.js';
import {
  ANIMATION,
  BACKGROUND_VISIBILITY,
  MOTION,
  PATH_VISIBILITY,
  RENDERING,
  TEXT_VISIBILITY,
  VIDEO_EXPORT,
  WAYPOINT_VISIBILITY,
} from '../config/constants.js';
import { bipolarLog2ValueToSlider } from '../utils/sliderScales.js';
import { Waypoint } from '../models/Waypoint.js';
import { Scene } from '../models/Scene.js';
import { ImageAsset, IMAGE_LIMITS } from '../models/ImageAsset.js';
import { PROJECT_ARCHIVE_LIMITS } from '../services/ImageAssetService.js';
import { STORAGE_LIMITS } from '../services/StorageService.js';
import {
  advanceEditRevision,
  beginAsyncProjectOperation,
  isAsyncProjectOperationCurrent,
} from './operationGeneration.js';
import { releaseBackgroundBusy, syncShowImageControls } from './backgroundLoading.js';
import { assertSafeStoredColor } from '../utils/safeColor.js';
import { assertPersistedEntityId, ENTITY_ID_LIMITS } from '../utils/entityId.js';
import { isImageCoordinateInRange } from '../utils/imageCoordinates.js';
import { formatBackgroundOverlay, setRangeReadout } from '../utils/uiReadouts.js';
import { resolveRenderReference } from '../utils/renderReference.js';
import { resolvePathHeadImage } from '../utils/pathHeadPresets.js';
import { buildExampleProjects } from '../examples/index.js';
import {
  keepUnrestoredAutosave,
  keptEarlierNote,
  recoveryFailureGuidance,
} from './unrestoredAutosave.js';
import { clearRouteSchedules, settleSavedTiming } from './pathTiming.js';
import { regionOperation } from './regionLines.js';
import {
  captureRecoveryRevision,
  endBackgroundPrompt,
  finishRecoveryRevision,
  readRecoveryImages,
  recoveryRevisionSnapshot,
  releaseRestoredImages,
  recoveryRevisionSource,
  reportRecoveryRestore,
  reportRecoveryWrite,
  resetRecoveryWarnings,
  revisionIsDeferred,
  revisionOmissions,
  scheduleRecoveryGarbage,
  settleRecoveryRevision,
} from './recoveryImages.js';

export const PROJECT_MODEL_LIMITS = Object.freeze({
  MAX_ENTITY_ID_LENGTH: ENTITY_ID_LIMITS.MAX_LENGTH,
  MAX_WAYPOINTS: 2000,
  MAX_AREA_POINTS_PER_WAYPOINT: 256,
  MAX_AREA_POINTS_TOTAL: 10000,
  MAX_TREE_DEPTH: 64,
  MAX_TREE_NODES: 100000,
  MAX_STRING_LENGTH: 100000,
  MAX_STRING_BYTES_TOTAL: 2 * 1024 * 1024,
  MAX_EXPORT_DIMENSION: 16384,
  MAX_FRAME_RATE: 120,
  MAX_ANIMATION_DURATION_MS: 24 * 60 * 60 * 1000,
  MAX_ANIMATION_SPEED: 10000,
  MAX_WAYPOINT_PAUSE_MS: 10 * 60 * 1000,
  MAX_TOTAL_PAUSE_MS: 24 * 60 * 60 * 1000,
});

const FORBIDDEN_OBJECT_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

const CANONICAL_PROJECT_DEFAULTS = Object.freeze({
  styles: Object.freeze({
    pathColor: RENDERING.DEFAULT_PATH_COLOR,
    pathThickness: RENDERING.DEFAULT_PATH_THICKNESS,
    pathStyle: 'solid',
    pathShape: 'line',
    markerStyle: 'dot',
    dotColor: RENDERING.DEFAULT_PATH_COLOR,
    dotSize: RENDERING.DEFAULT_DOT_SIZE,
    beaconStyle: 'none',
    labelMode: TEXT_VISIBILITY.FADE_UP,
    graphicsScale: 1,
    showPathCasing: true,
    // A project saved before Hold at end existed plays as it did: no hold
    // (CROWD-06). A new project starts at ANIMATION.HOLD_AT_END_DEFAULT_MS.
    holdAtEndMs: 0,
  }),
  pathHead: Object.freeze({
    style: 'arrow',
    color: '#111111',
    size: RENDERING.PATH_HEAD_SIZE,
    image: null,
    imageAssetId: null,
    rotationMode: 'auto',
    rotationOffset: 0,
  }),
  pathGlow: Object.freeze({ enabled: false, intensity: RENDERING.PATH_GLOW_DEFAULT_INTENSITY }),
  exportSettings: Object.freeze({
    frameRate: VIDEO_EXPORT.DEFAULT_FRAME_RATE,
    format: 'mp4',
    resolutionX: 1920,
    resolutionY: 1080,
    backgroundZoom: 100,
    includeCamera: true,
    includeText: true,
  }),
  motionSettings: Object.freeze({
    pathVisibility: PATH_VISIBILITY.SHOW_ON_PROGRESSION,
    pathTrail: MOTION.PATH_TRAIL_DEFAULT,
    waypointVisibility: WAYPOINT_VISIBILITY.HIDE_BEFORE,
    backgroundVisibility: BACKGROUND_VISIBILITY.ALWAYS_SHOW,
    revealSize: MOTION.SPOTLIGHT_SIZE_DEFAULT,
    revealFeather: MOTION.SPOTLIGHT_FEATHER_DEFAULT,
    revealTrail: MOTION.SPOTLIGHT_TRAIL_DEFAULT,
    aovAngle: MOTION.AOV_ANGLE_DEFAULT,
    aovDistance: MOTION.AOV_DISTANCE_DEFAULT,
    aovDropoff: MOTION.AOV_DROPOFF_DEFAULT,
  }),
});

function assertSafeProjectTree(root) {
  const stack = [{ value: root, depth: 0 }];
  const visited = new WeakSet();
  let nodeCount = 0;
  let stringBytes = 0;

  while (stack.length > 0) {
    const { value, depth } = stack.pop();
    nodeCount += 1;
    if (nodeCount > PROJECT_MODEL_LIMITS.MAX_TREE_NODES || depth > PROJECT_MODEL_LIMITS.MAX_TREE_DEPTH) {
      throw new Error('Project metadata is too deeply nested or complex');
    }
    if (typeof value === 'number' && !Number.isFinite(value)) {
      throw new Error('Project contains a non-finite numeric value');
    }
    if (typeof value === 'string') {
      if (value.length > PROJECT_MODEL_LIMITS.MAX_STRING_LENGTH) {
        throw new Error('Project contains an oversized text value');
      }
      stringBytes += new TextEncoder().encode(value).length;
      if (stringBytes > PROJECT_MODEL_LIMITS.MAX_STRING_BYTES_TOTAL) {
        throw new Error('Project text exceeds the 2 MB limit');
      }
      continue;
    }
    if (value == null || typeof value === 'boolean' || typeof value === 'number') continue;
    if (typeof value !== 'object') {
      throw new Error('Project contains an unsupported value type');
    }
    if (visited.has(value)) throw new Error('Project metadata contains a cycle');
    visited.add(value);

    const keys = Object.keys(value);
    for (const key of keys) {
      if (FORBIDDEN_OBJECT_KEYS.has(key)) {
        throw new Error(`Project contains a forbidden object key: ${key}`);
      }
      stack.push({ value: value[key], depth: depth + 1 });
    }
  }
}

/**
 * Validate project metadata without charging separately bounded bitmap payloads
 * against the text-field budget. Older recovery records stored original image
 * data URLs inline; those bytes are still validated by ImageAsset's MIME,
 * signature, byte and pixel limits before they can enter live state.
 *
 * @param {Object} projectData
 */
function assertSafeProjectEnvelope(projectData) {
  const metadata = { ...projectData };
  if (typeof metadata.backgroundImage === 'string') {
    metadata.backgroundImage = null;
  }
  if (Array.isArray(metadata.imageAssets)) {
    metadata.imageAssets = metadata.imageAssets.map(asset => {
      if (!asset || typeof asset !== 'object' || Array.isArray(asset)) return asset;
      return typeof asset.base64 === 'string' ? { ...asset, base64: null } : asset;
    });
  }
  assertSafeProjectTree(metadata);
}

function safeClone(value) {
  if (value == null) return value;
  assertSafeProjectTree(value);
  return JSON.parse(JSON.stringify(value));
}

function stageWaypoints(data) {
  const waypointData = data ?? [];
  if (!Array.isArray(waypointData)) throw new Error('Invalid project waypoints');
  if (waypointData.length > PROJECT_MODEL_LIMITS.MAX_WAYPOINTS) {
    throw new Error(`Project waypoint limit is ${PROJECT_MODEL_LIMITS.MAX_WAYPOINTS}`);
  }

  const ids = new Set();
  let areaPointCount = 0;
  let totalPauseMs = 0;
  return waypointData.map((serialized, index) => {
    if (!Waypoint.validate(serialized)) {
      throw new Error(`Invalid waypoint at index ${index}`);
    }
    if (serialized.id != null) {
      assertPersistedEntityId(serialized.id, `waypoint id at index ${index}`);
      if (ids.has(serialized.id)) throw new Error(`Duplicate waypoint id at index ${index}`);
      ids.add(serialized.id);
    }
    for (const field of ['segmentColor', 'dotColor']) {
      assertSafeStoredColor(serialized[field], `waypoint ${field} at index ${index}`, {
        allowTransparent: true,
      });
    }
    for (const field of ['labelColor', 'labelBgColor']) {
      assertSafeStoredColor(serialized[field], `waypoint ${field} at index ${index}`);
    }
    assertSafeStoredColor(
      serialized.areaHighlight?.fillColor,
      `waypoint area fillColor at index ${index}`,
      { allowTransparent: true }
    );
    assertSafeStoredColor(
      serialized.areaHighlight?.borderColor,
      `waypoint area borderColor at index ${index}`,
      { allowTransparent: true }
    );
    const numericFields = [
      'segmentWidth', 'segmentSpeed', 'shapeAmplitude', 'shapeFrequency',
      'dotSize', 'rippleThickness', 'rippleMaxScale', 'pulseAmplitude',
      'pulseCycleSpeed', 'labelOffsetX', 'labelOffsetY', 'labelWidth',
      'labelSize', 'labelBgOpacity', 'pauseTime', 'customImageRotationOffset',
      'created', 'modified',
    ];
    for (const field of numericFields) {
      if (field in serialized && !Number.isFinite(Number(serialized[field]))) {
        throw new Error(`Invalid waypoint ${field} at index ${index}`);
      }
    }
    const boundedFields = {
      segmentWidth: [0, 100], segmentSpeed: [0.1, 10],
      shapeAmplitude: [0, 100], shapeFrequency: [1, 20], dotSize: [0, 100],
      rippleThickness: [0, 100], rippleMaxScale: [0, 10000],
      pulseAmplitude: [0, 100], pulseCycleSpeed: [0.01, 600],
      labelOffsetX: [-1000, 1000], labelOffsetY: [-1000, 1000],
      labelWidth: [0, 100], labelSize: [1, 500], labelBgOpacity: [0, 1],
      pauseTime: [0, PROJECT_MODEL_LIMITS.MAX_WAYPOINT_PAUSE_MS],
      customImageRotationOffset: [-1000000, 1000000],
    };
    for (const [field, [minimum, maximum]] of Object.entries(boundedFields)) {
      if (field in serialized && (Number(serialized[field]) < minimum || Number(serialized[field]) > maximum)) {
        throw new Error(`Waypoint ${field} is outside the supported range at index ${index}`);
      }
    }
    totalPauseMs += Number(serialized.pauseTime ?? 0);
    if (totalPauseMs > PROJECT_MODEL_LIMITS.MAX_TOTAL_PAUSE_MS) {
      throw new Error(`Project pause-time budget is ${PROJECT_MODEL_LIMITS.MAX_TOTAL_PAUSE_MS} ms`);
    }
    if (serialized.camera) {
      if ('zoom' in serialized.camera && !Number.isFinite(Number(serialized.camera.zoom))) {
        throw new Error(`Invalid waypoint camera zoom at index ${index}`);
      }
      if ('zoom' in serialized.camera &&
          (Number(serialized.camera.zoom) < 1 || Number(serialized.camera.zoom) > 64)) {
        throw new Error(`Waypoint camera zoom is outside the supported range at index ${index}`);
      }
    }
    if (serialized.areaHighlight) {
      for (const field of ['centerX', 'centerY', 'radius', 'width', 'height', 'fillOpacity',
        'borderWidth', 'fadeInMs', 'fadeOutMs']) {
        if (field in serialized.areaHighlight && !Number.isFinite(Number(serialized.areaHighlight[field]))) {
          throw new Error(`Invalid waypoint area ${field} at index ${index}`);
        }
      }
      // A new area is centred on its waypoint, which may sit off the image (DEF-03).
      for (const field of ['centerX', 'centerY']) {
        if (field in serialized.areaHighlight && !isImageCoordinateInRange(Number(serialized.areaHighlight[field]))) {
          throw new Error(`Waypoint area ${field} is outside the supported range at index ${index}`);
        }
      }
      if ('fillOpacity' in serialized.areaHighlight &&
          (Number(serialized.areaHighlight.fillOpacity) < 0 || Number(serialized.areaHighlight.fillOpacity) > 1)) {
        throw new Error(`Waypoint area fillOpacity is outside the supported range at index ${index}`);
      }
      for (const field of ['radius', 'width', 'height']) {
        if (field in serialized.areaHighlight &&
            (Number(serialized.areaHighlight[field]) < 0 || Number(serialized.areaHighlight[field]) > 2)) {
          throw new Error(`Waypoint area ${field} is outside the supported range at index ${index}`);
        }
      }
      for (const field of ['fadeInMs', 'fadeOutMs']) {
        if (field in serialized.areaHighlight &&
            (Number(serialized.areaHighlight[field]) < 0 || Number(serialized.areaHighlight[field]) > 600000)) {
          throw new Error(`Waypoint area ${field} is outside the supported range at index ${index}`);
        }
      }
    }
    const points = serialized.areaHighlight?.points ?? [];
    if (!Array.isArray(points) || points.length > PROJECT_MODEL_LIMITS.MAX_AREA_POINTS_PER_WAYPOINT) {
      throw new Error(`Waypoint polygon-point limit is ${PROJECT_MODEL_LIMITS.MAX_AREA_POINTS_PER_WAYPOINT}`);
    }
    areaPointCount += points.length;
    if (areaPointCount > PROJECT_MODEL_LIMITS.MAX_AREA_POINTS_TOTAL) {
      throw new Error(`Project polygon-point limit is ${PROJECT_MODEL_LIMITS.MAX_AREA_POINTS_TOTAL}`);
    }
    for (const point of points) {
      // Vertices drawn in the margin while zoomed out lie off the image (DEF-03).
      if (!point || !isImageCoordinateInRange(Number(point.x)) || !isImageCoordinateInRange(Number(point.y))) {
        throw new Error(`Invalid waypoint polygon point at index ${index}`);
      }
    }
    const normalized = { ...serialized, customImage: null };
    for (const field of numericFields) {
      if (field in normalized) normalized[field] = Number(normalized[field]);
    }
    if (serialized.camera) {
      normalized.camera = { ...serialized.camera };
      if ('zoom' in normalized.camera) normalized.camera.zoom = Number(normalized.camera.zoom);
    }
    if (serialized.areaHighlight) {
      normalized.areaHighlight = { ...serialized.areaHighlight };
      for (const field of ['centerX', 'centerY', 'radius', 'width', 'height', 'fillOpacity',
        'borderWidth', 'fadeInMs', 'fadeOutMs']) {
        if (field in normalized.areaHighlight) normalized.areaHighlight[field] = Number(normalized.areaHighlight[field]);
      }
      normalized.areaHighlight.points = points.map(point => ({ x: Number(point.x), y: Number(point.y) }));
    }
    // Serialized projects may carry the historical `customImage: null` field,
    // but live image objects are hydrated exclusively from validated assets.
    return Waypoint.fromJSON(normalized);
  });
}

function assertProjectSettings(data) {
  const assertFiniteFields = (object, fields, label) => {
    if (object == null) return;
    if (typeof object !== 'object' || Array.isArray(object)) throw new Error(`Invalid ${label}`);
    for (const field of fields) {
      if (field in object && !Number.isFinite(Number(object[field]))) {
        throw new Error(`Invalid ${label} ${field}`);
      }
    }
  };
  const exportSettings = data.exportSettings ?? {};
  for (const field of ['resolutionX', 'resolutionY']) {
    if (field in exportSettings && (!Number.isFinite(Number(exportSettings[field])) ||
        Number(exportSettings[field]) < 1 || Number(exportSettings[field]) > PROJECT_MODEL_LIMITS.MAX_EXPORT_DIMENSION)) {
      throw new Error(`Invalid export ${field}`);
    }
  }
  if ('frameRate' in exportSettings && (!Number.isFinite(Number(exportSettings.frameRate)) ||
      Number(exportSettings.frameRate) < 1 || Number(exportSettings.frameRate) > PROJECT_MODEL_LIMITS.MAX_FRAME_RATE)) {
    throw new Error('Invalid export frame rate');
  }
  if ('backgroundZoom' in exportSettings && (!Number.isFinite(Number(exportSettings.backgroundZoom)) ||
      Number(exportSettings.backgroundZoom) <= 0 || Number(exportSettings.backgroundZoom) > 1000)) {
    throw new Error('Invalid background zoom');
  }
  const animation = data.animationState ?? {};
  if ('mode' in animation && !['constant-speed', 'constant-time'].includes(animation.mode)) {
    throw new Error('Invalid animation mode');
  }
  if ('speed' in animation && (!Number.isFinite(Number(animation.speed)) || Number(animation.speed) <= 0 ||
      Number(animation.speed) > PROJECT_MODEL_LIMITS.MAX_ANIMATION_SPEED)) {
    throw new Error('Invalid animation speed');
  }
  if ('duration' in animation && (!Number.isFinite(Number(animation.duration)) || Number(animation.duration) < 0 ||
      Number(animation.duration) > PROJECT_MODEL_LIMITS.MAX_ANIMATION_DURATION_MS)) {
    throw new Error('Invalid animation duration');
  }
  assertFiniteFields(data.background, ['overlay'], 'background');
  if (data.background && (Number(data.background.overlay ?? 0) < -100 ||
      Number(data.background.overlay ?? 0) > 100)) {
    throw new Error('Invalid background overlay');
  }
  assertFiniteFields(data.styles, ['pathThickness', 'dotSize', 'graphicsScale', 'holdAtEndMs'], 'style');
  assertFiniteFields(data.styles?.pathHead, ['size', 'rotationOffset'], 'path-head style');
  assertFiniteFields(data.styles?.pathGlow, ['intensity'], 'path-glow style');
  assertSafeStoredColor(data.styles?.pathColor, 'style pathColor');
  assertSafeStoredColor(data.styles?.dotColor, 'style dotColor');
  assertSafeStoredColor(data.styles?.pathHead?.color, 'path-head colour');
  if (data.styles?.graphicsScale != null &&
      (Number(data.styles.graphicsScale) <= 0 || Number(data.styles.graphicsScale) > 16)) {
    throw new Error('Invalid graphics scale');
  }
  if (data.styles?.holdAtEndMs != null &&
      (Number(data.styles.holdAtEndMs) < 0 || Number(data.styles.holdAtEndMs) > ANIMATION.HOLD_AT_END_MAX_MS)) {
    throw new Error('Invalid hold at end');
  }
  assertFiniteFields(data.motionSettings, [
    'pathTrail', 'revealSize', 'revealFeather', 'revealTrail',
    'aovAngle', 'aovDistance', 'aovDropoff'
  ], 'motion setting');

  for (const [value, label] of [
    [data.renderReference, 'render reference'],
    [data.timingReference, 'timing reference'],
  ]) {
    if (value == null) continue;
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error(`Invalid ${label}`);
    }
    const width = Number(value.width);
    const height = Number(value.height);
    if (!Number.isFinite(width) || width <= 0 || width > PROJECT_MODEL_LIMITS.MAX_EXPORT_DIMENSION ||
        !Number.isFinite(height) || height <= 0 || height > PROJECT_MODEL_LIMITS.MAX_EXPORT_DIMENSION) {
      throw new Error(`Invalid ${label}`);
    }
  }
}

/**
 * The checks a project's model passes to open, run without opening it: its
 * size and shape, its settings, its waypoints and its scene, each as staging
 * checks them (staging runs this first). An action about to change the
 * project can run it on the project it would make, and refuse one that
 * would not reopen (DEF-52).
 * @param {Object} projectData - A project snapshot
 * @returns {{ waypoints: Array<Waypoint>, scene: Scene }} Staged, not installed
 */
export function stageProjectModel(projectData) {
  if (!projectData || typeof projectData !== 'object' || Array.isArray(projectData)) {
    throw new Error('Invalid project data');
  }
  assertSafeProjectEnvelope(projectData);
  assertProjectSettings(projectData);
  return {
    waypoints: stageWaypoints(projectData.waypoints),
    scene: Scene.fromJSON(projectData.scene || {}),
  };
}

async function stageProject(app, projectData, { backgroundBase64 = null, imageAssets = null } = {}) {
  const { waypoints, scene } = stageProjectModel(projectData);
  let stagedAssets = imageAssets;
  if (stagedAssets == null) {
    const serializedAssets = projectData.imageAssets ?? [];
    if (!Array.isArray(serializedAssets)) throw new Error('Invalid image asset collection');
    if (serializedAssets.length > 0 && typeof app.imageAssetService.stageFromJSON !== 'function') {
      throw new Error('Image asset staging is unavailable');
    }
    stagedAssets = serializedAssets.length > 0
      ? await app.imageAssetService.stageFromJSON(serializedAssets)
      : [];
  }
  if (!Array.isArray(stagedAssets)) throw new Error('Invalid staged image assets');
  if (stagedAssets.length > PROJECT_ARCHIVE_LIMITS.MAX_ASSETS) {
    throw new Error(`Project image-asset limit is ${PROJECT_ARCHIVE_LIMITS.MAX_ASSETS}`);
  }
  let stagedAssetBytes = 0;
  let stagedAssetPixels = 0;
  for (const asset of stagedAssets) {
    if (!(asset instanceof ImageAsset)) throw new Error('Invalid staged image asset');
    stagedAssetBytes += ImageAsset.assertValidSerialized(asset).byteLength;
    stagedAssetPixels += asset.width * asset.height;
  }
  if (stagedAssetBytes > PROJECT_ARCHIVE_LIMITS.MAX_ASSET_BYTES_TOTAL) {
    throw new Error('Project image assets exceed the 40 MB total limit');
  }
  if (stagedAssetPixels > PROJECT_ARCHIVE_LIMITS.MAX_IMAGE_PIXELS_TOTAL) {
    throw new Error('Project image assets exceed the decoded pixel budget');
  }
  const assetsById = new Map(stagedAssets.map(asset => [asset.id, asset]));
  if (assetsById.size !== stagedAssets.length) throw new Error('Duplicate image asset id');

  for (const waypoint of waypoints) {
    if (!waypoint.customImageAssetId) continue;
    const asset = assetsById.get(waypoint.customImageAssetId);
    if (!asset) throw new Error(`Missing custom image for waypoint ${waypoint.id}`);
    waypoint.customImage = await asset.getImageElement();
  }

  const stylesData = safeClone(projectData.styles || {});
  // A null Graphics scale is an absent one, so it takes the default 1×
  // (DEF-37): `Number(null)` stored 0, which the next save would write and
  // load refuses.
  if (stylesData.graphicsScale === null) delete stylesData.graphicsScale;
  // Likewise a null Hold at end is an absent one: no hold (CROWD-06).
  if (stylesData.holdAtEndMs === null) delete stylesData.holdAtEndMs;
  const styles = {
    ...CANONICAL_PROJECT_DEFAULTS.styles,
    ...stylesData,
    pathHead: {
      ...CANONICAL_PROJECT_DEFAULTS.pathHead,
      ...(stylesData?.pathHead || {}),
      image: null,
    },
    pathGlow: {
      ...CANONICAL_PROJECT_DEFAULTS.pathGlow,
      ...(stylesData?.pathGlow || {}),
    },
  };
  for (const field of ['pathThickness', 'dotSize', 'graphicsScale', 'holdAtEndMs']) {
    if (field in stylesData) styles[field] = Number(stylesData[field]);
  }
  for (const field of ['size', 'rotationOffset']) {
    if (field in (stylesData.pathHead || {})) styles.pathHead[field] = Number(stylesData.pathHead[field]);
  }
  if (stylesData.pathGlow && 'intensity' in stylesData.pathGlow) {
    styles.pathGlow = { ...styles.pathGlow, intensity: Number(stylesData.pathGlow.intensity) };
  }
  const headAssetId = styles.pathHead.imageAssetId;
  if (headAssetId && !assetsById.has(headAssetId)) {
    throw new Error('Missing custom path-head image');
  }
  styles.pathHead.image = await resolvePathHeadImage(styles.pathHead, assetId => {
    const asset = assetsById.get(assetId);
    if (!asset) throw new Error('Missing custom path-head image');
    return asset.getImageElement();
  });

  let backgroundImage = null;
  const backgroundDataURL = backgroundBase64 || projectData.backgroundImage || null;
  if (backgroundDataURL) {
    backgroundImage = await ImageAsset.decodeDataURL(backgroundDataURL, 'project background');
  }

  const exportSettingsData = safeClone(projectData.exportSettings || {});
  for (const field of ['frameRate', 'resolutionX', 'resolutionY', 'backgroundZoom']) {
    if (field in exportSettingsData) exportSettingsData[field] = Number(exportSettingsData[field]);
  }
  const motionSettingsData = safeClone(projectData.motionSettings || {});
  for (const field of ['pathTrail', 'revealSize', 'revealFeather', 'revealTrail',
    'aovAngle', 'aovDistance', 'aovDropoff']) {
    if (field in motionSettingsData) motionSettingsData[field] = Number(motionSettingsData[field]);
  }

  const exportSettings = { ...CANONICAL_PROJECT_DEFAULTS.exportSettings, ...exportSettingsData };
  // The project's own visual reference, or null: one with none adopts the
  // canvas it is shown on as the commit fits it, not the canvas before the
  // load (`commitStagedProject`; UI-06 PR 6).
  const renderReference = finishRenderReference(
    scene, projectData.renderReference,
    projectData.timingReference
  );

  // The saved base timeline. A project with a crowd, fewer than two route
  // waypoints and none opens with the default, so its crowds can play
  // (CROWD-08 part A): by a file, browser recovery or an example, which all
  // stage here. A positive one opens as saved, however far past the Duration
  // slider's reach. Opening alone changes nothing the author made, so it does
  // not mark the project changed.
  let duration = Number(projectData.animationState?.duration ?? 0);
  if (duration === 0 && waypoints.length < 2 && scene.getFlowLayers().length > 0) {
    duration = ANIMATION.DEFAULT_DURATION;
  }

  return {
    waypoints,
    scene,
    assets: stagedAssets,
    styles,
    background: {
      image: backgroundImage,
      overlay: Number(projectData.background?.overlay ?? 0),
      fit: projectData.background?.fit === 'fill' ? 'fill' : 'fit',
      // Show image unticked (UI-08). A save from before it opens shown, the
      // owner's word: its exportSettings.pathOnly is no longer read.
      hidden: projectData.background?.hidden === true,
    },
    exportSettings,
    renderReference,
    motionSettings: { ...CANONICAL_PROJECT_DEFAULTS.motionSettings, ...motionSettingsData },
    animationState: {
      mode: projectData.animationState?.mode || 'constant-speed',
      speed: Number(projectData.animationState?.speed ?? ANIMATION.DEFAULT_SPEED),
      duration,
    },
  };
}

function getUndoBaseline(staged) {
  const baseline = {
    waypoints: staged.waypoints.map(waypoint => waypoint.toJSON()),
    selectedWaypointId: null,
    selectedWaypointIds: [],
    styles: {
      ...staged.styles,
      pathHead: staged.styles.pathHead ? { ...staged.styles.pathHead, image: null } : undefined,
    },
    scene: staged.scene.toJSON(),
    // As `_getUndoableState`, byte for byte (CON-04): Show image (UI-08)
    backgroundHidden: staged.background.hidden === true,
  };
  // As every undo entry with no route keeps it (`_getUndoableState`, CROWD-08
  // part A): undoing back to the opened project restores its timeline.
  if (staged.waypoints.length < 2) baseline.baseDurationMs = staged.animationState.duration;
  return baseline;
}

/** Detached canonical Scene and exact archive proof for synchronous checked edits. */
export function prepareCheckedProjectEdit(app, projectData, action) {
  const candidate = JSON.parse(JSON.stringify(projectData));
  candidate.scene = stageProjectModel(candidate).scene.toJSON();
  // Constructor defaults/omission can alter tree size; validate the final shape too.
  stageProjectModel(candidate);
  const background = getRetainedBackgroundDataURL(app, action);
  const prepared = app.imageAssetService.assertArchiveFitsByBound(candidate, background);
  return { candidate, background, prepared };
}

/** Preserve an existing recovery instruction instead of obscuring it with unrelated advice. */
export function projectEditError(label, failure) {
  let message = failure.message;
  if (!/try again|Reload the PNG/.test(message)) {
    const advice = /image|background/i.test(message)
      ? 'Use a smaller background or fewer custom images, then try again.'
      : 'Shorten labels or remove unused nodes and paths, then try again.';
    message += `${message.endsWith('.') ? '' : '.'} ${advice}`;
  }
  return new Error(`${label}: ${message}`);
}

/**
 * Return the exact validated bytes that produced the live background image.
 *
 * The historical `_autosaveBackgroundCache` field is actually a short-lived
 * source-byte cache. It must never be populated by drawing the image to a
 * canvas: ZIP and HTML exports preserve the user's PNG/JPEG/WebP bytes, and
 * browser recovery keeps those same bytes in its image store (DEF-98), so a
 * restored background is exported as it was chosen.
 *
 * @param {Object} app
 * @param {string} [action]
 * @returns {string|null}
 */
export function getRetainedBackgroundDataURL(app, action = 'exporting') {
  const image = app.background?.image;
  if (!image) return null;

  const retained = app._autosaveBackgroundCache;
  const unavailableMessage =
    `Original background bytes are unavailable. Reload the PNG, JPEG, or WebP background before ${action}.`;
  if (retained?.image !== image || typeof retained.dataURL !== 'string') {
    throw new Error(unavailableMessage);
  }

  try {
    const metadata = ImageAsset.inspectDataURL(retained.dataURL);
    ImageAsset.assertValidSerialized({
      id: 'background',
      name: 'background',
      base64: retained.dataURL,
      width: Number(image.naturalWidth || image.width),
      height: Number(image.naturalHeight || image.height),
      mimeType: metadata.mimeType,
      size: metadata.byteLength,
    }, { maxBytes: PROJECT_ARCHIVE_LIMITS.MAX_BACKGROUND_BYTES });
  } catch (error) {
    console.error('Retained background source bytes are invalid:', error);
    throw new Error(unavailableMessage);
  }
  return retained.dataURL;
}

function getSerializedByteLength(value) {
  const serialized = JSON.stringify(value);
  if (typeof serialized !== 'string') throw new Error('Autosave snapshot is not serializable');
  return new TextEncoder().encode(serialized).length;
}

/**
 * Capture the project for browser recovery: one revision, the model, the
 * images it references and their names at once (DEF-98,
 * `src/app/recoveryImages.js`). Recovery keeps each image the live project
 * references, its bytes in the browser's image store and its file name in
 * the record, within limits no larger than a project file's; an image not
 * kept takes the built-in fallback it always had in the record and is
 * warned of by name. The record as it stands now must fit the 4 MiB limit.
 * @returns {{revision: Object|null, snapshot: Object|null, error: Error|null}}
 */
function prepareRecoveryWrite(app) {
  settleSavedTiming(app);
  let revision = null;
  try {
    revision = captureRecoveryRevision(app);
    const snapshot = recoveryRevisionSnapshot(app, revision);
    if (getSerializedByteLength(snapshot) > STORAGE_LIMITS.AUTOSAVE_SERIALIZED_MAX) {
      throw new Error('Project model exceeds the autosave storage limit');
    }
    return { revision, snapshot, error: null };
  } catch (error) {
    finishRecoveryRevision(app, revision);
    return { revision: null, snapshot: null, error };
  }
}

// What browser recovery did or could not do must be heard. The author did not
// ask, and nothing says it again: the omission and auto-save warnings are
// suppressed once said, and a restore happens once per load. So the
// announcement queue never lets later routine messages displace it (DEF-45).
// The same notice said while its text still waits (a second project opened
// before the first one's warning was read) is read once, after both.
const RECOVERY_NOTICE = Object.freeze({ essential: true });

/**
 * A deferred record was written final: the status line, the images it did
 * not keep, and a tidy of the image store (DEF-98).
 */
function recoveryWritten(app, revision, outcome) {
  if (outcome?.provisional) {
    if (outcome.ok === false) reportAutosaveFailure(app);
    return;
  }
  if (outcome?.ok) {
    app._autosaveFailureWarningShown = false;
    app.setStatus?.(STATUS_RECOVERED);
    speakRecovered(app);
    reportRecoveryWrite(app, revisionOmissions(revision));
    scheduleRecoveryGarbage(app);
  } else {
    reportAutosaveFailure(app);
  }
}

function replaceImmediateRecovery(app) {
  if (!app.storageService.saveAutoSave) return { attempted: false, saved: false };

  const prepared = prepareRecoveryWrite(app);
  if (prepared.error) console.warn('Browser recovery snapshot could not be prepared:', prepared.error);

  let saved = false;
  const { revision } = prepared;
  if (revision) {
    // Images still being stored: the record is written at once, naming those
    // stored, so the previous session cannot come back; the final record
    // follows once they are (DEF-98). Otherwise it is final now.
    const deferred = revisionIsDeferred(revision) && settleRecoveryRevision(app, revision) !== null &&
      typeof app.storageService.saveAutoSaveDeferred === 'function';
    if (!deferred) reportRecoveryWrite(app, revisionOmissions(revision));
    try {
      if (deferred) {
        app._recoveryRevision = revision;
        saved = app.storageService.saveAutoSaveDeferred(recoveryRevisionSource(app, revision),
          outcome => recoveryWritten(app, revision, outcome));
      } else {
        saved = app.storageService.saveAutoSave(prepared.snapshot);
        if (saved) scheduleRecoveryGarbage(app);
      }
    } catch (error) {
      console.error('Failed to write browser recovery:', error);
    }
    if (!deferred || !saved) finishRecoveryRevision(app, revision);
  }
  if (!saved) app.storageService.clearAutoSave?.();
  return { attempted: true, saved };
}

function reportAutosaveFailure(app) {
  app.setStatus?.(STATUS_RECOVERY_FAILED);
  // A failure ends no episode: "Saved to browser recovery", once heard, is
  // not heard again until the next open or save (the owner, 2026-10-09;
  // Codex r2). The failure itself is announced below, once.
  statusEpisode(app).failed = true;
  if (app._autosaveFailureWarningShown) return;
  app._autosaveFailureWarningShown = true;
  // A kept record that could not be restored may be what stops the write
  // (DEF-28): it stays, and the report says what the author can do.
  app.announce('Auto-save failed. Save a project file to keep your work.' + recoveryFailureGuidance(app),
    'polite', RECOVERY_NOTICE);
}

/**
 * The header's status line (UI-06 B-17): the save state as text, beside the
 * title, where the "●" was the only sign. Recovery is said as recovery, never
 * as "saved": the only save is the project file (Codex).
 */
const STATUS_UNSAVED = 'Unsaved changes';
const STATUS_RECOVERED = 'Saved to browser recovery';
const STATUS_RECOVERY_FAILED = 'Browser recovery failed';

/**
 * What of the status line is spoken (the owner, 2026-10-09: "Speak changes,
 * not repeats"): through the one announcer, politely, "Unsaved changes" the
 * first time an opened, saved, cleared or freshly started project changes,
 * and "Saved to browser recovery" the first time recovery saves after that;
 * then nothing until the next episode. A recovery write that fails is
 * announced once (`reportAutosaveFailure`); recovery working again is heard
 * only if "Saved to browser recovery" has not been yet in the episode (Codex
 * r2). The line itself is not live (`aria-live="off"`) and shows each.
 * @param {Object} app
 * @returns {{unsaved: boolean, recovered: boolean, failed: boolean}} This episode's state
 */
function statusEpisode(app) {
  app._statusEpisode ??= { unsaved: false, recovered: false, failed: false };
  return app._statusEpisode;
}

/** An open, a save, Clear all or a restored start begins a new episode. */
export function beginStatusEpisode(app) {
  app._statusEpisode = { unsaved: false, recovered: false, failed: false };
}

/** The first change of an episode is heard. */
function speakUnsaved(app) {
  const episode = statusEpisode(app);
  if (episode.unsaved) return;
  episode.unsaved = true;
  app.announce?.(STATUS_UNSAVED);
}

/** The episode's first recovery save after a change, or after a failed one, is heard. */
function speakRecovered(app) {
  const episode = statusEpisode(app);
  if (episode.recovered || !(episode.unsaved || episode.failed)) return;
  episode.recovered = true;
  episode.failed = false;
  app.announce?.(STATUS_RECOVERED);
}

/**
 * Write the project to browser recovery (debounced), reporting a failure.
 * @param {Object} app
 * @returns {{ok: boolean, pending?: boolean, unchanged?: boolean, error?: Error}}
 */
function writeRecovery(app) {
  try {
    const prepared = prepareRecoveryWrite(app);
    if (prepared.error) console.warn('Auto-save recovery was reduced:', prepared.error);
    if (!prepared.revision) {
      app.storageService.cancelAutoSave?.();
      reportAutosaveFailure(app);
      return { ok: false, error: prepared.error };
    }

    const { revision } = prepared;
    if (revisionIsDeferred(revision) && typeof app.storageService.autoSaveDeferred === 'function') {
      // It names images: what it may name is decided as it is written, once
      // they are stored, and the `pagehide` flush writes what is stored by
      // then (DEF-98). What it left out is said once it is written.
      app._recoveryRevision = revision;
      return app.storageService.autoSaveDeferred(recoveryRevisionSource(app, revision),
        outcome => recoveryWritten(app, revision, outcome));
    }
    finishRecoveryRevision(app, revision);
    reportRecoveryWrite(app, revisionOmissions(revision));

    // StorageService reports the outcome of the actual delayed write. A
    // quota/security failure must never be presented or cached as success.
    const result = app.storageService.autoSave(prepared.snapshot, outcome => {
      if (outcome?.ok) {
        app._autosaveFailureWarningShown = false;
        app.setStatus?.(STATUS_RECOVERED);
        speakRecovered(app);
        scheduleRecoveryGarbage(app);
      } else {
        reportAutosaveFailure(app);
      }
    });
    if (result?.ok === false) reportAutosaveFailure(app);
    return result;
  } catch (e) {
    console.error('Error saving state:', e);
    app.storageService.cancelAutoSave?.();
    reportAutosaveFailure(app);
    return { ok: false, error: e };
  }
}

function captureLiveState(app) {
  const animationState = app.animationEngine.state;
  return {
    waypoints: app.waypoints,
    waypointMap: new Map(app.waypointsById || []),
    sceneLayers: app.scene.flowLayers,
    assets: app.imageAssetService.getAssets?.() || null,
    styles: app.styles,
    background: { ...app.background },
    exportSettings: { ...app.exportSettings },
    motionSettings: { ...app.motionSettings },
    renderReference: app.renderReference ? { ...app.renderReference } : null,
    selectedWaypoint: app.selectedWaypoint,
    selectedWaypoints: app.selectedWaypoints,
    selectedCrowd: app.selectedCrowd,
    pathPoints: app.pathPoints,
    canvasFit: app._canvasFit?.() ?? null,
    animation: { ...animationState },
    animationTransport: animationState.captureTransportState?.() || {
      timelineProgress: animationState.progress ?? 0,
      isPlaying: animationState.isPlaying ?? false,
      isPaused: animationState.isPaused ?? false,
      playbackSpeed: animationState.playbackSpeed ?? 1,
    },
    animationPauseState: app.animationEngine._currentPauseState
      ? { ...app.animationEngine._currentPauseState }
      : null,
    sceneEndParts: app.animationEngine.sceneEndParts ?? null,
    nextPauseIndex: app.animationEngine.nextPauseIndex,
    jklDirection: app._jklDirection,
    jklSpeedMultiplier: app._jklSpeedMultiplier,
    controllerJklDirection: app.jklDirection,
    controllerJklSpeed: app.jklSpeed,
    undo: app.undoService?.createSnapshot?.() || null,
    isDirty: app._isDirty,
    backgroundCache: app._autosaveBackgroundCache,
    // What browser recovery keeps of the background and what a restore left
    // out: staged with the project, put back with it (DEF-98).
    backgroundName: app._backgroundName ?? null,
    recoveryCarried: app._recoveryCarried ?? [],
    majorWaypointsCache: app._majorWaypointsCache,
    waypointProgressCache: app._waypointProgressCache,
    timingDerived: app._timingDerived,
  };
}

function replaceObjectContents(target, snapshot) {
  for (const key of Object.keys(target)) {
    if (!(key in snapshot)) delete target[key];
  }
  Object.assign(target, snapshot);
}

function runRollbackStep(label, action) {
  try {
    action();
  } catch (error) {
    console.error(`Project rollback could not restore ${label}:`, error);
  }
}

function restoreLiveState(app, previous) {
  // The model first, by plain assignment, which cannot fail; then the image
  // assets and the undo history, each a step of its own, so neither can stop
  // the rest being restored (DEF-49).
  app.waypoints = previous.waypoints;
  if (app.waypointsById) {
    app.waypointsById.clear();
    previous.waypointMap.forEach((value, key) => app.waypointsById.set(key, value));
  }
  app.scene.flowLayers = previous.sceneLayers;
  app.styles = previous.styles;
  replaceObjectContents(app.background, previous.background);
  replaceObjectContents(app.exportSettings, previous.exportSettings);
  replaceObjectContents(app.motionSettings, previous.motionSettings);
  app.selectedWaypoint = previous.selectedWaypoint;
  app.selectedWaypoints = previous.selectedWaypoints;
  app.selectedCrowd = previous.selectedCrowd;
  app.pathPoints = previous.pathPoints;
  app.renderReference = previous.renderReference;
  app._isDirty = previous.isDirty;
  app._autosaveBackgroundCache = previous.backgroundCache;
  app._backgroundName = previous.backgroundName;
  app._recoveryCarried = previous.recoveryCarried;
  app._majorWaypointsCache = previous.majorWaypointsCache;
  app._waypointProgressCache = previous.waypointProgressCache;
  app._timingDerived = previous.timingDerived;
  if (app.animationEngine?.state) Object.assign(app.animationEngine.state, previous.animation);
  if (app.animationEngine) app.animationEngine.sceneEndParts = previous.sceneEndParts;
  app._jklDirection = previous.jklDirection;
  app._jklSpeedMultiplier = previous.jklSpeedMultiplier;
  app.jklDirection = previous.controllerJklDirection;
  app.jklSpeed = previous.controllerJklSpeed;
  runRollbackStep('the image assets', () => {
    if (previous.assets && app.imageAssetService.replaceAssets) {
      app.imageAssetService.replaceAssets(previous.assets);
    }
  });
  runRollbackStep('the undo history', () => {
    if (previous.undo && app.undoService?.restoreSnapshot) {
      app.undoService.restoreSnapshot(previous.undo);
    }
  });

  // A late commit failure may happen after transform, path, and controls have
  // already switched to the staged project. Restore each derived/UI surface
  // independently so one secondary rollback error never hides the load error
  // or prevents the remaining surfaces from being repaired.
  const restoredProject = {
    styles: previous.styles,
    background: previous.background,
    exportSettings: previous.exportSettings,
    motionSettings: previous.motionSettings,
    animationState: previous.animation,
  };
  // The canvas first: the commit fitted it to the staged project's export
  // size (UI-06 PR 6), and the image's transform is measured on it.
  runRollbackStep('the canvas size', () => app._restoreCanvasFit?.(previous.canvasFit));
  runRollbackStep('the background transform', () => {
    if (hasReferenceMotion(app.scene)) {
      app.coordinateTransform?.setBackgroundZoom?.(previous.exportSettings.backgroundZoom / 100);
    }
    app.updateImageTransform?.(previous.background.image ?? null);
  });
  runRollbackStep('the animation transport', () => {
    if (app.animationEngine.restoreTransportState) {
      app.animationEngine.restoreTransportState(previous.animationTransport);
    }
    if (previous.animationPauseState && app.animationEngine._currentPauseState) {
      replaceObjectContents(app.animationEngine._currentPauseState, previous.animationPauseState);
    }
    app.animationEngine.nextPauseIndex = previous.nextPauseIndex;
  });
  runRollbackStep('the layer controls', () => app.updateLayersStrip?.());
  runRollbackStep('the global style controls', () => app._syncGlobalStyleUI?.());
  runRollbackStep('the project controls', () => syncLoadedProjectControls(app, restoredProject));
  runRollbackStep('the transport controls', () => {
    app.uiController?.setPlaybackSpeed?.(previous.animation.playbackSpeed ?? 1);
    app._updatePlayPauseUI?.();
    app.updateTimeDisplay?.(previous.animation.currentTime, previous.animation.duration);
    app.updateDurationReadout?.();
  });
  // Play, the export items and the Guide card, as the restored project allows (CROWD-08 part A).
  runRollbackStep('the play availability', () => app.refreshPlayAvailability?.());
  runRollbackStep('the selection controls', () => {
    app.uiController?.setSelection?.(previous.selectedWaypoints || [], previous.selectedWaypoint);
    if (app.interactionHandler?.setSelection) {
      app.interactionHandler.setSelection(
        previous.selectedWaypoints || [],
        previous.selectedWaypoint
      );
    } else {
      app.interactionHandler?.setSelectedWaypoint?.(previous.selectedWaypoint);
    }
  });
  runRollbackStep('the waypoint list', () => app.updateWaypointList?.());
  runRollbackStep('the waypoint editor', () => app.updateWaypointEditor?.());
  runRollbackStep('the title', () => app.updateTitleIndicator?.());
  runRollbackStep('the canvas', () => app.render?.());
}

function syncLoadedProjectControls(app, staged) {
  if (app.elements?.headPreview) {
    const assetId = staged.styles.pathHead?.imageAssetId;
    const asset = assetId ? app.imageAssetService.getAsset(assetId) : null;
    app.elements.headPreview.style.display = asset ? 'block' : 'none';
    if (app.elements.headFilename) app.elements.headFilename.textContent = asset?.name || '';
    if (app.elements.headPreviewImg) app.elements.headPreviewImg.src = asset?.base64 || '';
  }

  syncShowImageControls(app, staged.background);
  const exportSettings = staged.exportSettings;
  if (app.elements?.exportIncludeCamera) app.elements.exportIncludeCamera.checked = exportSettings.includeCamera !== false;
  if (app.elements?.exportIncludeText) app.elements.exportIncludeText.checked = exportSettings.includeText !== false;
  if (app.elements?.exportFrameRate) app.elements.exportFrameRate.value = exportSettings.frameRate;
  if (app.elements?.exportResX) app.elements.exportResX.value = exportSettings.resolutionX;
  if (app.elements?.exportResY) app.elements.exportResY.value = exportSettings.resolutionY;
  if (app.elements?.backgroundZoom) app.elements.backgroundZoom.value = exportSettings.backgroundZoom;
  setRangeReadout(app.elements?.backgroundZoom, app.elements?.backgroundZoomValue, `${exportSettings.backgroundZoom}%`);
  app.coordinateTransform?.setBackgroundZoom?.(exportSettings.backgroundZoom / 100);

  if (app.elements?.bgFitToggle) {
    app.elements.bgFitToggle.textContent = staged.background.fit === 'fit' ? 'Fit' : 'Fill';
    app.elements.bgFitToggle.dataset.mode = staged.background.fit;
  }
  if (app.elements?.bgOverlay) {
    const sliderValue = bipolarLog2ValueToSlider(
      staged.background.overlay, MOTION.TINT_MIN, MOTION.TINT_MAX
    );
    app.elements.bgOverlay.value = String(sliderValue);
    setRangeReadout(
      app.elements.bgOverlay,
      app.elements.bgOverlayValue,
      formatBackgroundOverlay(staged.background.overlay)
    );
  }

  if (app.elements?.pathVisibility) app.elements.pathVisibility.value = staged.motionSettings.pathVisibility;
  if (app.elements?.pathTrail && app.uiController?.trailFractionToSlider) {
    app.elements.pathTrail.value = app.uiController.trailFractionToSlider(staged.motionSettings.pathTrail);
  }
  app.uiController?.setTrailValue?.(staged.motionSettings.pathTrail);
  app.uiController?.updateTrailControlVisibility?.(staged.motionSettings.pathVisibility);
  if (app.elements?.waypointVisibility) app.elements.waypointVisibility.value = staged.motionSettings.waypointVisibility;
  if (app.elements?.backgroundVisibility) app.elements.backgroundVisibility.value = staged.motionSettings.backgroundVisibility;
  // The reveal and Angle of View sliders, and the Duration thumb, follow the
  // project too: a thumb left where the last project put it, under this
  // project's readout, jumps the setting at the first touch (DEF-20). The
  // Duration slider on screen is the right one; the left one was removed.
  app.uiController?.syncRevealControls?.(staged.motionSettings);
  app.uiController?.syncAngleOfViewControls?.(staged.motionSettings);
  if (app.elements?.animationSpeedRight) {
    // With no route the thumb is the base timeline's (CROWD-08 part A); the
    // route's payload is as it was.
    if ((app.waypoints?.length || 0) >= 2) app.eventBus?.emit('ui:slider:update-speed', staged.animationState.speed);
    else app.syncDurationThumb?.();
  }
}

function syncLoadedProjectUI(app, staged) {
  app.updateLayersStrip?.();
  app._syncGlobalStyleUI?.();
  syncLoadedProjectControls(app, staged);
}

function commitStagedProject(app, staged, { markClean = false } = {}) {
  const previous = captureLiveState(app);
  try {
    app._installingFinishContext = hasReferenceMotion(staged.scene);
    if (app.imageAssetService.replaceAssets) {
      app.imageAssetService.replaceAssets(staged.assets);
    } else if (staged.assets.length > 0) {
      throw new Error('Image asset commit is unavailable');
    }

    app.waypoints = staged.waypoints;
    if (app.waypointsById) {
      app.waypointsById.clear();
      staged.waypoints.forEach(waypoint => app.waypointsById.set(waypoint.id, waypoint));
    }
    app.scene.flowLayers = staged.scene.flowLayers;
    app.styles = staged.styles;
    Object.assign(app.background, staged.background);
    Object.assign(app.exportSettings, staged.exportSettings);
    Object.assign(app.motionSettings, staged.motionSettings);
    app.selectedWaypoint = null;
    app.selectedWaypoints = [];
    app.selectedCrowd = null;
    app.pathPoints = [];
    app.renderReference = staged.renderReference;
    app._majorWaypointsCache = null;
    app._autosaveBackgroundCache = staged.background.image && staged.backgroundSourceDataURL
      ? { image: staged.background.image, dataURL: staged.backgroundSourceDataURL }
      : null;
    // The background's file name, kept with it in browser recovery, and
    // what the record being restored left out (DEF-98).
    app._backgroundName = staged.background.image ? (staged.backgroundName ?? null) : null;
    app._recoveryCarried = staged.recoveryCarried ?? [];

    if (app._installingFinishContext) {
      app.coordinateTransform?.setBackgroundZoom?.(staged.exportSettings.backgroundZoom / 100);
    }
    app.updateImageTransform?.(staged.background.image ?? null);
    // The previous project's schedules and timeline (its intro, comet tail
    // and path travel time), and whether its duration was a rebuild's, are
    // not this one's: it opens with the timing it was saved with, and a
    // constant-time duration stays the author's until timing is rebuilt for
    // its route (DEF-06).
    clearRouteSchedules(app);
    app.animationEngine.clearIntroTime?.();
    app.animationEngine.clearTailTime?.();
    app.animationEngine.pathDuration = 0;
    app._timingDerived = false;
    app.animationEngine.setMode?.(staged.animationState.mode);
    app.animationEngine.setSpeed?.(staged.animationState.speed);
    // The saved duration is the base timeline (CROWD-05); what the scene
    // waits for after it is measured once the path below is in place.
    app.animationEngine.setDuration?.(staged.animationState.duration);
    app.animationEngine.setPlaybackSpeed?.(1);
    // The fit below supersedes the refit work held or due (a banner's refit
    // or a rebuild held while playing, a resize's debounce): dropped before
    // the pause would run it, so that fit is the one rebuild (UI-06 PR 6;
    // Codex r3 on bc2d3a4, 5).
    app._supersedeRefitWork?.();
    app.animationEngine.pause?.();
    app.animationEngine.seekToProgress?.(0);
    app.uiController?.setPlaybackSpeed?.(1);
    app.uiController?.setSelection?.([], null);
    if (app.interactionHandler?.setSelection) app.interactionHandler.setSelection([], null);
    else app.interactionHandler?.setSelectedWaypoint?.(null);

    // The canvas takes this project's export size before its path is laid
    // out, as a fresh start's takes its image's: it kept the last fit until a
    // window resize, or the first banner's refit, changed it and the timing
    // with it (UI-06 PR 6, the lead's settlement on 753f93f). The fit lays
    // out a route of two or more waypoints; a video export keeps its own
    // canvas (the fit's guard). Always: a project of fewer than two
    // waypoints clears what the last route fed (DEF-06).
    app._installingFinishContext = false;
    const fitted = app.updateCanvasAspectRatio?.() === true;
    if (!fitted || app.waypoints.length < 2) app.calculatePath?.();
    // A project with no visual reference of its own has adopted the canvas
    // it is shown on, the fit's (the fit adopts one): the same project
    // opened twice takes the same one, never the last project's canvas. With
    // no fit, the canvas as it is, else the export size, as before.
    if (!resolveRenderReference(app.renderReference)) {
      app.renderReference = resolveRenderReference(
        { width: app.displayWidth, height: app.displayHeight },
        { width: app.exportSettings.resolutionX, height: app.exportSettings.resolutionY }
      );
    }
    syncLoadedProjectUI(app, staged);
    app.updateWaypointList?.();
    app.render?.();

    app.undoService?.reset?.(getUndoBaseline(staged));
    app.pruneImageAssets?.();
    app._isDirty = markClean ? false : app._isDirty;
    app.updateTitleIndicator?.();
  } catch (error) {
    try {
      app._installingFinishContext = false;
      restoreLiveState(app, previous);
    } catch (rollbackError) {
      console.error('Project rollback failed:', rollbackError);
    }
    throw error;
  }
}

/**
 * What follows a commit. The staged project is live by now, so a failure
 * here is not a project that failed to load (DEF-28 keeps a recovery record
 * only when the commit itself failed).
 * @param {Object} app
 */
function completeProjectReplacement(app) {
  // Warnings are once-per-project, not once per browser session. A freshly
  // opened/recovered baseline must be able to warn about its own omissions.
  resetRecoveryWarnings(app);
  app._autosaveFailureWarningShown = false;
  // The last project's prompt for its background is not this one's (DEF-98).
  endBackgroundPrompt(app);
  // And its first change is heard (UI-06 B-17).
  beginStatusEpisode(app);

  // These cancellations happen only after every operation that can reject the
  // commit. A failed load therefore retains pending autosave and undo work.
  app.storageService.cancelAutoSave?.();
  if (app._undoDebounceTimer) {
    clearTimeout(app._undoDebounceTimer);
    app._undoDebounceTimer = null;
  }
  app.eventBus?.emit('project:replaced');
}

export const persistenceMixin = {
  
  /**
   * Save project as ZIP file (includes all images and settings)
   */
  async saveProject() {
    const saveRevision = this._editRevision || 0;
    const saveGeneration = this._projectGeneration || 0;
    try {
      this.announce('Saving project...');
      this.setStatus?.('Saving project…');
      
      // ZIP assets are archived separately; the canonical model builder owns
      // every other field so explicit save, recovery and HTML export cannot
      // drift on additive project metadata such as the render reference.
      settleSavedTiming(this);
      const projectData = this._buildProjectSnapshot({ includeAssets: false });
      
      // Preserve the original validated PNG/JPEG/WebP bytes exactly. Export
      // must fail rather than silently substituting a canvas re-encoding.
      const backgroundBase64 = getRetainedBackgroundDataURL(this, 'saving the project');
      
      // Export as ZIP
      const zipBlob = await this.imageAssetService.exportZip(projectData, backgroundBase64, 'route-project');
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `route-project-${timestamp}.zip`;
      
      // Download
      this.imageAssetService.downloadZip(zipBlob, filename);
      
      // ZIP generation is asynchronous. Only mark clean when the live project
      // is still the exact revision captured above; later edits or a new
      // project baseline are not present in the downloaded file.
      const sameProject = (this._projectGeneration || 0) === saveGeneration;
      const unchanged = sameProject && (this._editRevision || 0) === saveRevision;
      if (unchanged) {
        this._isDirty = false;
        this.updateTitleIndicator?.();
        this.setStatus?.('Project saved');
        this.announce('Project saved');
        beginStatusEpisode(this);
      } else if (sameProject) {
        this.setStatus?.(STATUS_UNSAVED);
        this.announce('Project file saved; newer changes remain unsaved.');
      }
      console.log(`📦 Project saved: ${filename}`);
    } catch (err) {
      console.error('Failed to save project:', err);
      this.setStatus?.('Failed to save project');
      this.announce(`Failed to save project: ${err.message}`);
    }
  },
  
  /**
   * Open one of the bundled example projects (DEMO-01).
   *
   * Fetched as the very archive a user downloads and opened through the same
   * `loadProject` path their own save takes — no special case anywhere, which
   * is what makes the examples worth having as fixtures.
   *
   * @param {string} exampleId
   * @returns {Promise<boolean>} True when the project loaded
   */
  async loadExampleProject(exampleId) {
    const example = buildExampleProjects().find(each => each.id === exampleId);
    if (!example) {
      this.announce('That example is not available.');
      return false;
    }
    // The archive not downloaded, with the status when the site answered one
    // (none when the fetch itself failed, offline): it says what to do next
    // (UI-06 B-16, the owner's words).
    const downloadFailed = status => Object.assign(new Error(status ? `HTTP ${status}` : 'download failed'),
      { downloadFailed: true, status });
    try {
      this.announce(`Opening ${example.name}…`);
      this._beginOpening?.(example.name);
      const response = await fetch(`examples/${example.id}.zip`, { cache: 'no-store' })
        .catch(() => { throw downloadFailed(); });
      if (!response.ok) throw downloadFailed(response.status);
      const blob = await response.blob();
      // Named so the load log and any error name the example, not "undefined".
      const file = typeof File === 'function'
        ? new File([blob], `${example.id}.zip`, { type: 'application/zip' })
        : Object.assign(blob, { name: `${example.id}.zip` });
      // Its background is the bundled image, by that image's file name, never
      // the archive's (DEF-98).
      const backgroundName = decodeURIComponent(String(example.backgroundSource || '').split('/').pop() || '') || null;
      return await this.loadProject(file, { backgroundName });
    } catch (err) {
      console.error('Failed to open example project:', err);
      // One message, with the reason, heard once as the toast it is (UI-06 J-05).
      this._endOpening?.(`Could not open ${example.name}`);
      const message = err.downloadFailed
        ? `Could not open ${example.name}${err.status ? ` (HTTP ${err.status})` : ''}. Check your connection and try again.`
        : `Could not open ${example.name}: ${err.message}`;
      this.eventBus.emit('ui:toast', { message, priority: 'assertive' });
      return false;
    }
  },

  /**
   * Load project from ZIP file
   * @param {File} file - ZIP file to load
   * @param {{backgroundName?: string|null}} [options] - The background's file
   *   name, where the caller knows it better than the archive does (an example)
   */
  async loadProject(file, { backgroundName = null } = {}) {
    const operation = beginAsyncProjectOperation(this, 'project-load', { replaceProject: true });
    // No background request made for the project this replaces can commit now.
    releaseBackgroundBusy();
    try {
      this.announce('Loading project...');
      if (!this._openingLabel) this._beginOpening?.('project');
      
      // ZIP and bitmap work returns detached objects. Only the synchronous
      // commit below can replace live project state.
      const imported = await this.imageAssetService.importZip(file);
      if (!isAsyncProjectOperationCurrent(this, operation)) return false;
      const staged = await stageProject(this, imported.projectData, {
        backgroundBase64: imported.backgroundBase64,
        imageAssets: imported.imageAssets,
      });
      if (!isAsyncProjectOperationCurrent(this, operation)) return false;
      staged.backgroundSourceDataURL = imported.backgroundBase64 || null;
      // The background's file name as the project file carries it, never the
      // project file's own name (DEF-98; Codex, brief round 1, 7).
      staged.backgroundName = backgroundName ?? imported.backgroundName ?? null;
      // The commit and its message, one operation after the awaits above: the
      // message says the change, so the regions' lines the open moves are not
      // said again; a selection made while it waited is (UI-06 PR 6; Codex r2
      // on 753f93f, 3).
      regionOperation(this, cover => {
        commitStagedProject(this, staged, { markClean: true });
        completeProjectReplacement(this);

        // Replace the previous session's recovery point with the newly loaded
        // project. If the browser quota rejects it, remove the stale recovery
        // point so a reload cannot resurrect the old project.
        const recovery = replaceImmediateRecovery(this);
        const recoveryUnavailable = recovery.attempted && !recovery.saved;

        this._endOpening?.(recoveryUnavailable ? STATUS_RECOVERY_FAILED : STATUS_RECOVERED);
        if (recoveryUnavailable) {
          this.announce('Project loaded, but browser recovery is unavailable. Save the project file to keep it safe.'
            + recoveryFailureGuidance(this), 'polite', RECOVERY_NOTICE);
        } else {
          this.announce('Project loaded');
        }
        cover();
      });
      console.log(`📦 Project loaded: ${file.name} (${this.waypoints.length} waypoints, ${this.imageAssetService.getAssetCount()} assets)`);
      return true;
    } catch (err) {
      if (!isAsyncProjectOperationCurrent(this, operation)) return false;
      console.error('Failed to load project:', err);
      this._endOpening?.(`Could not open ${this._openingLabel || 'project'}`);
      this.announce('Failed to load project: ' + err.message);
      return false;
    }
  },

  /**
   * Mark the project as having unsaved changes and update title indicator
   * Per UI spec §2.1: Append ● dot to title when dirty
   */
  markDirty() {
    advanceEditRevision(this);
    // Each edit is unsaved until the recovery write lands (UI-06 B-17); the
    // first of an episode is heard.
    this.setStatus?.(STATUS_UNSAVED);
    speakUnsaved(this);
    if (!this._isDirty) {
      this._isDirty = true;
      this.updateTitleIndicator();
    }
  },
  
  /**
   * Mark the project as saved (no unsaved changes)
   */
  markClean() {
    if (this._isDirty) {
      this._isDirty = false;
      this.updateTitleIndicator();
    }
  },
  
  /**
   * Show or hide the unsaved-changes mark, and keep the status line true to
   * the dirty flag (UI-06 B-17). The title is the app's name alone; the "●"
   * beside it is decorative, and the status text carries the meaning: it
   * cannot say "Unsaved changes" of a clean project, and a project made dirty
   * by a rollback says so.
   */
  updateTitleIndicator() {
    const titleEl = document.getElementById('app-title');
    if (!titleEl) return;
    // No title: Help shows the version, by keyboard too (UI-06 G-10)
    titleEl.textContent = 'Route Plotter';
    const glyph = document.getElementById('title-indicator');
    if (glyph) glyph.hidden = !this._isDirty;
    if (!this._isDirty && this._status === STATUS_UNSAVED) this.setStatus?.('');
    if (this._isDirty && !this._status) this.setStatus?.(STATUS_UNSAVED);
  },

  /**
   * Write the header's status line (UI-06 B-17): the save or recovery state,
   * or what the app is doing (opening, saving, exporting). Not a live region:
   * what the author must hear is announced by the path that sets it. Called
   * optionally throughout: a host that borrows one method of this mixin
   * alone (the persistence tests bind a bare object) has no status line.
   * @param {string} text
   */
  setStatus(text) {
    this._status = text;
    const line = document.getElementById('app-status');
    if (line) line.textContent = text;
  },

  /**
   * Say what is being opened, by the name the author chose it by, until
   * `_endOpening` says how it went.
   * @param {string} label
   * @private
   */
  _beginOpening(label) {
    this._openingLabel = label;
    this.setStatus?.(`Opening ${label}…`);
  },

  /** @private */
  _endOpening(text) {
    this._openingLabel = null;
    this.setStatus?.(text);
  },

  /**
   * Build the canonical coordVersion-9 project snapshot.
   *
   * Single source of the project model shape. Explicit exports can include
   * custom image assets; browser recovery calls this with includeAssets=false,
   * keeps the images it references in its own store, and strips only the
   * references to images it could not keep (DEF-98, recoveryImages.js).
   *
   * @param {Object} [options]
   * @param {boolean} [options.includeAssets=true] - Include custom image assets.
   * @returns {Object} Serialisable project data (coordVersion 9)
   */
  _buildProjectSnapshot({ includeAssets = true } = {}) {
    // Create a clean copy of styles without the pathHead image object (but keep imageAssetId)
    const stylesCopy = { ...this.styles };
    if (stylesCopy.pathHead) {
      stylesCopy.pathHead = { ...stylesCopy.pathHead, image: null };
    }

    const timingReference = resolveRenderReference(
      { width: this.displayWidth, height: this.displayHeight },
      { width: this.exportSettings.resolutionX, height: this.exportSettings.resolutionY }
    );
    const renderReference = resolveRenderReference(
      this.renderReference,
      timingReference,
      { width: this.exportSettings.resolutionX, height: this.exportSettings.resolutionY }
    );

    const snapshot = {
      // v9: layered scene (v7 + additive `scene` block; 8 skipped — the
      // fork's local builds used it for graph-only saves)
      coordVersion: 9,
      waypoints: this.waypoints.map(wp => wp.toJSON()), // Serialize Waypoint instances
      scene: this.scene.toJSON(), // Flow-layer params + seeds only — runtime dot state never persists
      styles: stylesCopy,
      animationState: {
        mode: this.animationEngine.state.mode,
        speed: this.animationEngine.state.speed,
        // The base timeline, not the playback duration (CROWD-05): what the
        // scene waits for after it is recomputed on open, so a file opens as
        // before and a save and reopen never adds to it.
        duration: this.animationEngine.state.baseDuration ?? this.animationEngine.state.duration
        // Note: playbackSpeed intentionally NOT saved - resets to 1x on each session
      },
      background: {
        overlay: this.background.overlay,
        fit: this.background.fit,
        hidden: this.background.hidden === true
      },
      exportSettings: {
        frameRate: this.exportSettings.frameRate,
        resolutionX: this.exportSettings.resolutionX,
        resolutionY: this.exportSettings.resolutionY,
        backgroundZoom: this.exportSettings.backgroundZoom,
        includeCamera: this.exportSettings.includeCamera,
        includeText: this.exportSettings.includeText
      },
      motionSettings: {
        pathVisibility: this.motionSettings.pathVisibility,
        pathTrail: this.motionSettings.pathTrail,
        waypointVisibility: this.motionSettings.waypointVisibility,
        backgroundVisibility: this.motionSettings.backgroundVisibility,
        revealSize: this.motionSettings.revealSize,
        revealFeather: this.motionSettings.revealFeather,
        // Defaulted rather than copied through: a caller whose live settings
        // predate this property would otherwise write an explicit `undefined`,
        // which the snapshot validator reads as a present-but-invalid field
        // and refuses to load.
        revealTrail: this.motionSettings.revealTrail ?? MOTION.SPOTLIGHT_TRAIL_DEFAULT,
        aovAngle: this.motionSettings.aovAngle,
        aovDistance: this.motionSettings.aovDistance,
        aovDropoff: this.motionSettings.aovDropoff
      },
      // Include image assets if under size limit
      imageAssets: includeAssets ? this.imageAssetService.toJSON() : [],
      // Stable visual sizing space. Map-bound reference pixels scale from its
      // short edge; loading/rendering never mutates the authored values.
      renderReference,
      // Canvas dimensions the current timeline was derived from. Speed is
      // px/s against the on-screen path, so duration/markers depend on the
      // display size; the exported player recomputes timing in THIS space to
      // reproduce the authored timeline exactly, then renders at the export
      // resolution — the same rule as video export (_enterExportMode never
      // recalculates timing at the export canvas). Additive v9 field.
      timingReference
      // Note: Camera settings are per-waypoint, saved in waypoint.camera
    };
    return snapshot;
  },

  autoSave() {
    // Mark as dirty when changes are made
    this.markDirty();
    return writeRecovery(this);
  },

  /**
   * Write the project to browser recovery (debounced), without marking it
   * changed: for when recovery can be written again (DEF-28).
   */
  saveRecovery() {
    return writeRecovery(this);
  },
  
  async loadAutosave() {
    console.debug('📥 [loadAutosave] Loading saved state...');
    // A record that cannot be restored is kept for the author to download or
    // discard, never cleared or left for the next autosave to overwrite
    // (DEF-28). It is read once, as text, and each refusal below keeps that
    // same text: a second read could find another record. A record held
    // because it could not be restored, or one that may be, is on offer,
    // not tried again.
    const hold = this.storageService.holdState?.();
    if (hold && hold.state !== 'none') return false;
    const text = this.storageService.loadAutoSaveText();
    if (text === null) return false;
    const refuse = (error) => {
      console.warn('Autosave was not restored; current state was left unchanged:', error);
      return keepUnrestoredAutosave(this, text);
    };
    // What the restore pinned, let go of on every way out (DEF-98).
    let restoring = null;
    try {
      let data;
      try {
        data = JSON.parse(text);
      } catch (error) {
        return refuse(error);
      }

      const MIN_COORD_VERSION = 6;
      if (!data?.coordVersion || data.coordVersion < MIN_COORD_VERSION) {
        console.log('Old data version detected (v' + (data?.coordVersion || 1) + '); kept, not restored, for v' + MIN_COORD_VERSION);
        return keepUnrestoredAutosave(this, text);
      }

      // The images its manifest names, read from the browser's image store
      // and checked as an import checks them; an image that cannot come back
      // is isolated, its references alone taking the fallback (DEF-98). A
      // newer project opened, or the project cleared, while they are read
      // supersedes this restore.
      const operation = beginAsyncProjectOperation(this, 'recovery-restore');
      // A failure after an await is this restore's only while it is still
      // current: one a Clear All or a newer Open superseded keeps no record and
      // says nothing, since the project it would restore is gone (Codex, code
      // review round 2, 4). Either way what it pinned is let go.
      let images;
      try {
        images = await readRecoveryImages(this, data);
        restoring = images;
      } catch (error) {
        if (!isAsyncProjectOperationCurrent(this, operation)) return false;
        return refuse(error);
      }
      const abandon = (result) => {
        releaseRestoredImages(images);
        return result;
      };
      if (!isAsyncProjectOperationCurrent(this, operation)) return abandon(false);

      let staged;
      try {
        staged = await stageProject(this, images.data, images.kind === 'manifest'
          ? { backgroundBase64: images.backgroundBase64, imageAssets: images.imageAssets }
          : {});
      } catch (error) {
        if (!isAsyncProjectOperationCurrent(this, operation)) return abandon(false);
        return abandon(refuse(error));
      }
      if (!isAsyncProjectOperationCurrent(this, operation)) return abandon(false);
      staged.backgroundSourceDataURL = images.kind === 'manifest'
        ? images.backgroundBase64
        : (data.backgroundImage || null);
      staged.backgroundName = images.backgroundName;
      staged.recoveryCarried = images.omitted;
      // The commit and its message, one operation: the message says the
      // change, so the regions' lines the restore moves are not said again; a
      // commit that rolled back says no such message (UI-06 PR 6; Codex r2 on
      // 753f93f, 3).
      const failedCommit = regionOperation(this, cover => {
        try {
          commitStagedProject(this, staged);
        } catch (error) {
          return error;
        }
        let recovery = { attempted: false, saved: false };
        try {
          completeProjectReplacement(this);
          // The project is live: recovery's own warnings and writes never
          // turn this restore into a failure (DEF-98).
          try {
            // What did not come back is said, and a background that did not
            // is offered again.
            reportRecoveryRestore(this, images);
            // Legacy recovery points may contain image bytes and original
            // filenames: the record is rewritten at once in this release's
            // form, naming them in the image store, so a second reload keeps
            // them.
            recovery = replaceImmediateRecovery(this);
          } catch (error) {
            console.warn('Browser recovery could not be rewritten after the restore:', error);
          }
        } finally {
          // The project's own images are pinned now: the read's pins go.
          releaseRestoredImages(images);
        }
        console.debug(`📷 Loaded ${staged.assets.length} image assets`);
        console.debug('Loaded waypoints:', staged.waypoints.length);
        console.debug('Loaded flow layers:', staged.scene.getFlowLayers().length);
        if (recovery.attempted && !recovery.saved) {
          this.announce('Previous session restored, but browser recovery is now unavailable. Save a project file to keep it safe.'
            + recoveryFailureGuidance(this), 'polite', RECOVERY_NOTICE);
        } else {
          // Announced over a record kept earlier, so it names that record too.
          const earlier = keptEarlierNote(this);
          this.announce(earlier ? `Previous session restored.${earlier}` : 'Previous session restored',
            earlier ? 'assertive' : 'polite', RECOVERY_NOTICE);
        }
        cover();
        return null;
      });
      if (failedCommit) releaseRestoredImages(images);
      // The commit rolled back, so this record was not restored either.
      if (failedCommit) return refuse(failedCommit);
      return true;
    } catch (error) {
      // After the commit: the project is live, so there is no record to keep.
      console.warn('Restoring the autosave did not finish after it was loaded:', error);
      return false;
    } finally {
      releaseRestoredImages(restoring);
    }
  },
};
